import AutoNumeric from "autonumeric";
import { IndicatorTIQColumnsHelper } from "../helpers/indicator-tiq-columns-helpers";
import { OptionHelpers } from "../helpers/option-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { IChartDataService } from "../models/base/chart-data-service.interface";
import { IndicatorColorType } from "../models/data-point/ti-indicator-data.model";
import { EMACloudUIManager } from "../models/ema-cloud/ema-cloud-ui.manager";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { IndicatorTIQColumnModel } from "../models/indicator/indicator-tiq-column.model";
import { ManageIndicatorModel } from "../models/indicator/manage-indicator.model";
import { IComponent } from "./component.interface";
declare const $: any;

export class IndicatorManagerPopupComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private element: HTMLElement | null = null;
    private base: IChartContainer;
    private service: IChartDataService;
    private model = new ManageIndicatorModel();
    private indicatorTIQColumns: Array<IndicatorTIQColumnModel> = [];
    private emaCloudUIManager: EMACloudUIManager | null = null;

    private isEmaCloud = false;
    private editScreen = false;

    private filterOutIndicators = [
        IndicatorType.EMACloud,
        IndicatorType.Stochastics,
        IndicatorType.DistanceFromEMA,
        IndicatorType.PivotPoint,
        IndicatorType.AVWAP,
        IndicatorType.RelativeVolume,
        IndicatorType.HighChartPivotPoints,
        IndicatorType.HighChartIKH,
        IndicatorType.HighChartAD,
        IndicatorType.HighChartMFI,
        IndicatorType.HighChartVolume,
    ];

    constructor(base: IChartContainer) {
        this.base = base;
        this.service = base.chartService;

        this.subscribeChartService();
        this.onBodyClick();

        if (!this.base.marketingWebChartMode) {
            this.onResize();
        }
    }

    public isActive(): boolean {
        return this.element?.classList.contains('active') ?? false;
    }

    public show() {
        if (!this.element) return;

        this.toAdd();
        this.element?.classList.add('active');
    }

    public close() {
        if (!this.element) return;

        this.destroyColorPickers();
        this.element.classList.remove('active');
    }

    public showEMACloud() {
        this.element?.classList.add('active');
        this.toEdit();
    }

    public syncEMACloud(save: boolean) {
        this.initEMACloudUIManager(save);
    }

    public quickAdd(indicatorType: IndicatorType, overrideValueOne?: number) {
        this.setAreaOptions();
        this.setAddModel(indicatorType);

        if (!isNaN(overrideValueOne) && overrideValueOne > 0) {
            this.model.valueOne = overrideValueOne;
        }

        this.addIndicators();
    }

    public quickRemove(indicatorType: IndicatorType) {
        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        const ind = indicators.find(x => x.indicatorType == indicatorType);
        if (ind) {
            this.removeIndicator(ind.id);
        }
    }

    private subscribeChartService() {
        if (this.base.isLiveData) {
            this.service.subscribeInitChartIndicatorsTIQData((indicators: Array<IndicatorTIQColumnModel>) => {
                this.indicatorTIQColumns = indicators;
                this.create();
            });
        } else {
            let init = false;
            this.service.subscribeInitChartData(() => {
                if (!init) {
                    init = true;
                    this.indicatorTIQColumns = IndicatorTIQColumnsHelper.getTIQColumns();
                    this.create();
                }
            });
        }
    }

    private create() {
        const optHtml = this.getOptionsHtml();
        const div = document.createElement('div');
        div.className = `settings-popup indicator-manager-popup`;
        div.innerHTML = `
        <div class="wrapper">
            <div class="head">
                <p class="title">Indicator Manager</p>
                <button class="close" type="button">
                    <span class="icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="17" height="17" fill="currentColor">
                            <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                            <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                        </svg>
                    </span>
                </button>
            </div>
            <div class="content">
                <div class="left-panel custom-scroll">
                    <ul>
                        ${optHtml}
                    </ul>
                </div>
                <div class="right-panel custom-scroll"></div>
            </div>
            <div class="footer">
                <button class="edit">Edit</button>
                <button class="revert">Revert</button>
                <button class="save mgr-8">Save</button>
                <button class="delete">Delete</button>
                <button class="add">Add</button>
            </div>
        </div>
        `;

        this.element = div;

        this.base.chartWrapper?.insertAdjacentElement('beforeend', this.element);
        setTimeout(() => {
            this.onClick();
            if (!this.base.marketingWebChartMode) {
                this.setContentMaxHeight();
            }
        }, 200);
    }

    private toEdit() {
        let optHtml = ``;

        if (this.base.tiChartSeries.getAllActiveIndicatorOptions().some(x => x.value == IndicatorType.EMACloud)) {
            optHtml += `
            <li data-value="28">
                <svg class="ico-trade-wave">
                    <use xlink:href="${this.base.basePath}assetfiles/img/trade-wave-v2.svg#trade-wave-v2-obj"></use>
                </svg>
                <span>${this.base.tiChartSeries?.chartStyleSettings?.tradeWaveName || 'TradeWave'}</span>
            </li>`;
        }

        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        indicators.forEach(indicator => {
            optHtml += `<li data-value="${indicator.id}">${this.getEditIndicatorName(indicator)}</li>`;
        });

        const ulElemeent = this.element?.querySelector<HTMLElement>('ul');
        if (ulElemeent) {
            ulElemeent.innerHTML = optHtml;
        }

        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (rightPanel) {
            rightPanel.innerHTML = '';
        }

        const editButton = this.element?.querySelector<HTMLElement>('.footer .edit');
        if (editButton) {
            editButton.innerHTML = 'Back';
        }

        this.element.classList.remove('add')
        this.element.classList.remove('edit')
        this.editScreen = true;

        this.onIndicatorItemClick();
    }

    private getEditIndicatorName(indicator: ManageIndicatorModel): string {
        const tiqIndicator = this.indicatorTIQColumns.find(x => x.displayName?.toLowerCase()?.indexOf(indicator.name.toLowerCase()) == 0);

        return tiqIndicator?.abbreviatedName?.replace('[periods]', indicator.valueOne?.toFixed(0) ?? '')
            .replace('[fast_periods]', indicator.valueOne?.toFixed(0) ?? '')
            .replace('[slow_periods]', indicator.valueTwo?.toFixed(0) ?? '') ?? indicator.name;
    }

    private getOptionsHtml(): string {
        let optHtml = ``;

        if (!this.base.tiChartSeries.getAllActiveIndicatorOptions().some(x => x.value == IndicatorType.EMACloud)) {
            optHtml += `
            <li data-value="28">
                <svg class="ico-trade-wave">
                    <use xlink:href="${this.base.basePath}assetfiles/img/trade-wave-v2.svg#trade-wave-v2-obj"></use>
                </svg>
                <span>${this.base.tiChartSeries?.chartStyleSettings?.tradeWaveName || 'TradeWave'}</span>
            </li>`;
        }

        const indicatorOptions = OptionHelpers.getIndicatorOptions(this.base.tiChartSeries?.chartStyleSettings).sort((a, b) => {
            return a.text.localeCompare(b.text)
        });

        indicatorOptions.forEach(indicator => {
            const isValidIndicator = this.filterOutIndicators.findIndex(x => x == indicator.value) == -1;
            if (isValidIndicator) {
                optHtml += `<li data-value="${indicator.value}">${indicator.text}</li>`;
            }
        });

        return optHtml;
    }

    private toAdd() {
        const optHtml = this.getOptionsHtml();
        const ulElemeent = this.element?.querySelector<HTMLElement>('ul');
        if (ulElemeent) {
            ulElemeent.innerHTML = optHtml;
        }

        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (rightPanel) {
            rightPanel.innerHTML = '';
        }

        const editButton = this.element?.querySelector<HTMLElement>('.footer .edit');
        if (editButton) {
            editButton.innerHTML = 'Edit';
        }

        this.element.classList.remove('add')
        this.element.classList.remove('edit')
        this.editScreen = false;

        this.onIndicatorItemClick();
    }

    private addIndicators() {
        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        this.setChartAreaNameIfNewChartArea();

        if (this.model.highChartIndicator) {
            this.base.tiChartSeries.addHighChartIndicator(this.model.indicatorType);
        }

        indicators.push(this.model);
        this.service.requestSaveIndicatorsForIndicatorManager(indicators);
    }

    private removeIndicator(id: string) {
        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        const ind = indicators.findIndex(x => x.id == id);
        if (ind > -1) {
            const indicator = indicators[ind];
            if (indicator.highChartIndicator) {
                this.base.tiChartSeries.removeHighChartIndicator(indicator.indicatorType);
            }
            
            indicators.splice(ind, 1);
        }

        this.service.requestSaveIndicatorsForIndicatorManager(indicators);
    }

    private setChartAreaNameIfNewChartArea() {
        if (this.model.targetChartArea?.toLowerCase() == 'newchartarea') {
            let max = this.base.marketingWebChartMode ? 0 : 1;
            for (const opt of this.areaOptions) {
                if (opt.indexOf('ChartArea') > -1) {
                    const value = parseInt(opt.replace('ChartArea', ''), 10);
                    if (value > max) {
                        max = value;
                    }
                }
            }

            this.model.targetChartArea = 'ChartArea' + (max + 1)
        }
    }

    private onClick() {
        this.element?.querySelectorAll<HTMLElement>('.close').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                this.close();
            });
        });

        this.element?.querySelectorAll<HTMLElement>('.edit').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.editScreen) {
                    this.toAdd();
                } else {
                    this.toEdit();
                }
            });
        });

        this.element?.querySelectorAll<HTMLElement>('.revert').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.isEmaCloud) {
                    this.emaCloudUIManager?.revertSettings();
                }
            });
        });

        this.element?.querySelectorAll<HTMLElement>('.add').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.isEmaCloud) {
                    this.emaCloudUIManager?.requestSaveSettings();
                } else {
                    this.addIndicators();
                }

                this.close();
            });
        });

        this.element?.querySelectorAll<HTMLElement>('.save').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.isEmaCloud) {
                    this.emaCloudUIManager?.requestSaveSettings();
                } else {
                    const indicators = this.service.getSavedIndicatorsForIndicatorManager();
                    if (this.editScreen) {
                        const ind = indicators.findIndex(x => x.id == this.model.id);
                        if (ind > -1) {
                            indicators[ind] = this.model;
                        }
                    }
                    
                    this.setChartAreaNameIfNewChartArea();
                    this.updateIndicatorsAbbrvWhenDirty();
                    this.service.requestSaveIndicatorsForIndicatorManager(indicators);
                }
            });
        });

        this.element?.querySelectorAll<HTMLElement>('.delete').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                const indicators = this.service.getSavedIndicatorsForIndicatorManager();
                const navigate = () => {
                    if (indicators.length || this.element.querySelector(`.left-panel ul li`)) {
                        this.toEdit();
                    } else {
                        this.toAdd();
                    }
                };
                if (this.isEmaCloud) {

                    this.base.chartService.requestSaveEMACloudSettings(null);
                    this.base.tiChartSeries.removeEMAClouldSeries();

                    setTimeout(() => {
                        const li = this.element.querySelector(`.left-panel ul li[data-value="${IndicatorType.EMACloud}"]`);
                        li?.remove();
                        navigate();
                    }, 800);
                } else {
                    const li = this.element.querySelector(`.left-panel ul li[data-value="${this.model.id}"]`);
                    li?.remove();
                    this.removeIndicator(this.model.id);
                    navigate();
                }
            });
        });
    }

    private updateIndicatorsAbbrvWhenDirty() {
        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        this.element?.querySelectorAll<HTMLElement>('ul li').forEach(el => {
            const indicator = indicators.find(x => x.id == el.dataset.value);
            if (indicator) {
                el.innerHTML = this.getEditIndicatorName(indicator);
            }
        });
    }

    private onIndicatorItemClick() {
        const listOptions = this.element?.querySelectorAll<HTMLElement>('li');
        listOptions.forEach((el: HTMLElement, i: number) => {
            if (i == 0) {
                el.classList.add('active');
                this.displayControl(el);
            }

            el.addEventListener('click', () => {
                listOptions.forEach(x => {
                    x.classList.remove('active');
                });

                el.classList.add('active');
                this.displayControl(el);
            });
        });
    }

    private initEMACloudUIManager(save: boolean) {
        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (!rightPanel) return;

        if (!this.emaCloudUIManager) {
            this.emaCloudUIManager = new EMACloudUIManager(this.base, rightPanel, true);
        }

        this.emaCloudUIManager.init(save);
    }

    private readonly defaultAreaOptions: Array<string> = [
        'NewChartArea',
        'MainChartArea'
    ];

    private areaOptions: Array<string> = [
        ...this.defaultAreaOptions
    ];

    private setAreaOptions() {
        this.areaOptions = [
            ...this.defaultAreaOptions
        ];

        const indicators = this.service.getSavedIndicatorsForIndicatorManager();
        for (const opt of indicators) {
            if (!this.areaOptions.some(x => x == opt.targetChartArea)) {
                this.areaOptions.push(opt.targetChartArea);
            }
        }
    }

    private setTitle(name: string) {
        const hTitle = this.element.querySelector('.head .title');
        if (hTitle) {
            hTitle.innerHTML = `Indicator Manager - ${name}`;
        }
    }

    private displayControl(el: HTMLElement) {
        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (!rightPanel) return;

        this.isEmaCloud = false;
        this.element.classList.remove('ema-cloud');

        const indicatorType: IndicatorType = parseInt(el.dataset.value, 10);
        if (indicatorType == IndicatorType.EMACloud) {
            this.initEMACloudUIManager(false);
            this.isEmaCloud = true;
            this.element.classList.add('ema-cloud');
        }

        this.editScreen ? this.element.classList.add('edit') : this.element.classList.add('add');
        this.setAreaOptions();
        if (this.isEmaCloud) {
            this.setTitle(`${this.base.tiChartSeries?.chartStyleSettings?.tradeWaveName || 'TradeWave'}`);
            return;
        }

        if (this.editScreen) {
            const indicators = this.service.getSavedIndicatorsForIndicatorManager();
            const opt = indicators.find(x => x.id == el.dataset.value);
            if (!opt) return;

            this.model = opt;
        } else {
            this.setAddModel(indicatorType);
        }

        this.setTitle(this.model.name);

        let optHtml = ``;
        let selectedArea = ``;
        for (let i = 0; i < this.areaOptions.length; i++) {
            let selected = false;
            if (this.areaOptions[i] == this.model.targetChartArea) {
                selected = true;
                selectedArea = this.areaOptions[i];
            }

            optHtml += `<li data-value="${this.areaOptions[i]}" class="${selected ? 'selected' : ''}">${this.areaOptions[i]}</li>`;
        }

        const html = `
            <div class="control">
                <span class="label-inline" style="width: 80px; margin-right: 4px;">Chart area</span>
                <div class="font-size-picker-wrapper chartarea mgr-8">
                    <div class="font-size-picker">
                        <div class="selected">
                            <p>${selectedArea}</p>
                        </div>
                        <div class="arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 7" width="11" height="7"
                                fill="none">
                                <path stroke="currentColor" stroke-width="1.3" d="M.5 1.5l5 4 5-4"></path>
                            </svg>
                        </div>
                        <ul class="selection">${optHtml}</ul>
                    </div>
                </div>
            </div>
            ${this.getColorGroupHtml()}
            ${this.getValueGroupHtml()}
        `;

        rightPanel.innerHTML = html;
        setTimeout(() => {
            this.initControls();
        }, 200);
    }

    private setAddModel(indicatorType: IndicatorType) {
        const indicatorOptions = OptionHelpers.getIndicatorOptions(this.base.tiChartSeries?.chartStyleSettings);
        const opt = indicatorOptions.find(x => x.value == indicatorType);
        if (!opt) return;

        this.model = new ManageIndicatorModel({
            name: opt.text,
            highChartIndicator: opt.highChartIndicator,
            indicatorType: opt.value,
            valueOne: opt.defaultValueOne,
            valueTwo: opt.defaultValueTwo
        });

        const tiqIndicator = this.indicatorTIQColumns.find(x => x.displayName?.toLowerCase()?.indexOf(opt.text?.toLowerCase()) == 0);
        if (this.model.indicatorType == IndicatorType.Volume) {
            if (this.base.marketingWebChartMode) {
                this.model.colorTypeOne = IndicatorColorType.UpDown;
            } else {
                this.model.colorOne = 'rgb(0, 0, 255)';
            }
        } else if (this.model.indicatorType == IndicatorType.MACD) {
            this.model.colorTypeOne = IndicatorColorType.One;
            this.model.colorOne = 'rgb(255, 153, 0)';
            this.model.colorTypeTwo = IndicatorColorType.One;
            this.model.colorTwo = 'rgb(0, 0, 255)';
            this.model.colorTypeThree = IndicatorColorType.UpDown;
            this.model.upColorThree = 'rgb(41, 243, 23)';
            this.model.downColorThree = 'rgb(243, 38, 23)';
        } else if (tiqIndicator) {
            this.model.colorTypeOne = tiqIndicator.colorType?.toLowerCase()?.indexOf('updown') > -1 ? IndicatorColorType.UpDown : IndicatorColorType.One;
            if (this.model.colorTypeOne == IndicatorColorType.UpDown) {
                if (this.model.indicatorType == IndicatorType.TIPatternRec) {
                    this.model.upColorThree = 'rgb(0, 255, 0)';
                    this.model.downColorThree = 'rgb(255, 0, 0)';
                } else {
                    this.model.upColorOne = tiqIndicator.upColor;
                    this.model.downColorOne = tiqIndicator.downColor;
                }
            } else {
                this.model.colorOne = tiqIndicator.color;
                if (this.model.indicatorType == IndicatorType.BBands || this.model.indicatorType == IndicatorType.KeltnerChannel) {
                    this.model.colorTypeTwo = IndicatorColorType.One;
                    this.model.colorTwo = tiqIndicator.color;
                }
            }
        }

        if (tiqIndicator && !tiqIndicator.prefersOwnPane) {
            this.model.targetChartArea = this.defaultAreaOptions[1];
        }
    }

    private toggleColorType() {
        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (!rightPanel || !rightPanel.innerHTML) return;

        const checkControl = (className: string) => {
            const value = $(rightPanel).find(`.radio-input.${className} input[type="radio"]:checked`).val();
            const valueAsInt = parseInt(value);
            const oneColor = valueAsInt == IndicatorColorType.One;
            if (oneColor) {
                $(rightPanel).find(`.control.${className}.color`).removeClass(`disabled`);
                $(rightPanel).find(`.control.${className}.up-color`).addClass(`disabled`);
                $(rightPanel).find(`.control.${className}.down-color`).addClass(`disabled`);
            } else {
                $(rightPanel).find(`.control.${className}.color`).addClass(`disabled`);
                $(rightPanel).find(`.control.${className}.up-color`).removeClass(`disabled`);
                $(rightPanel).find(`.control.${className}.down-color`).removeClass(`disabled`);
            }

            if (className == 'one') {
                this.model.colorTypeOne = oneColor ? IndicatorColorType.One : IndicatorColorType.UpDown;
            } else if (className == 'two') {
                this.model.colorTypeTwo = oneColor ? IndicatorColorType.One : IndicatorColorType.UpDown;
            } else {
                this.model.colorTypeThree = oneColor ? IndicatorColorType.One : IndicatorColorType.UpDown;
            }
        };

        checkControl('one');
        checkControl('two');
        checkControl('three');
    }

    private destroyColorPickers() {
        $(".sp-container").remove();
    }

    private initControls() {
        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (!rightPanel || !rightPanel.innerHTML) return;

        const ctx = this;
        $(rightPanel).find(".font-size-picker-wrapper.chartarea").off('click');
        $(rightPanel).find(".font-size-picker-wrapper.chartarea").on('click', (e: any) => {
            const hasActive = $(e.currentTarget).hasClass('active');
            ctx.hideAllPickerControls();
            if (!hasActive) {
                $(e.currentTarget).addClass("active");
            }
        });

        $(rightPanel).find(".font-size-picker-wrapper.chartarea li").off('click');
        $(rightPanel).find(".font-size-picker-wrapper.chartarea li").on('click', (e: any) => {
            const value = $(e.currentTarget).attr('data-value');

            this.model.targetChartArea = value;
            $(rightPanel).find('.font-size-picker-wrapper.chartarea .selected p').html(this.model.targetChartArea);
            $(rightPanel).find(".font-size-picker-wrapper.chartarea li").removeClass('selected');

            $(e.currentTarget).addClass('selected');
        });

        $(rightPanel).find(".section-head").on('click', (e: any) => {
            const parent = $(e.target).closest('.section');
            $(parent).toggleClass('active');
        });

        const getNormalColorValue = (className: string): string => {
            if (className == 'one') {
                return this.model.colorOne;
            } else if (className == 'two') {
                return this.model.colorTwo;
            }

            return this.model.colorThree;
        }

        const getUpColorValue = (className: string): string => {
            if (className == 'one') {
                return this.model.upColorOne;
            } else if (className == 'two') {
                return this.model.upColorTwo;
            }

            return this.model.upColorThree;
        }

        const getDownColorValue = (className: string): string => {
            if (className == 'one') {
                return this.model.downColorOne;
            } else if (className == 'two') {
                return this.model.downColorTwo;
            }

            return this.model.downColorThree;
        }

        const setNormalColorValue = (className: string, value: string) => {
            if (className == 'one') {
                this.model.colorOne = value;
            } else if (className == 'two') {
                this.model.colorTwo = value;
            } else if (className == 'three') {
                this.model.colorThree = value;
            }
        }

        const setUpColorValue = (className: string, value: string) => {
            if (className == 'one') {
                this.model.upColorOne = value;
            } else if (className == 'two') {
                this.model.upColorTwo = value;
            } else if (className == 'three') {
                this.model.upColorThree = value;
            }
        }

        const setDownColorValue = (className: string, value: string) => {
            if (className == 'one') {
                this.model.downColorOne = value;
            } else if (className == 'two') {
                this.model.downColorTwo = value;
            } else if (className == 'three') {
                this.model.downColorThree = value;
            }
        }

        const setupColorPicker = (className: string) => {
            const extraClassName = this.base.tiChartSeries?.chartStyleSettings?.isDarkTheme ? 'dark' : '';
            $(rightPanel).find(`.normal-color.${className} input`).spectrum('destroy');
            $(rightPanel).find(`.normal-color.${className} input`).spectrum({
                color: getNormalColorValue(className),
                showInput: true,
                showAlpha: true,
                containerClassName: extraClassName,
                change: (color: any) => {
                    if (color) {
                        let hex = `rgba(${color._r.toString()},${color._g.toString()},${color._b.toString()},${color._a.toString()})`;
                        setNormalColorValue(className, hex);
                        $(rightPanel).find(`.${className} .normal-color.color-picker`).css(`background-color`, hex);
                        $(rightPanel).find(`.${className} .normal-color.color-picker`).css(`background-image`, `unset`);
                    } else {
                        setNormalColorValue(className, '');
                        $(rightPanel).find(`.${className} .normal-color.color-picker`).css(`background-color`, ``);
                        $(rightPanel).find(`.${className} .normal-color.color-picker`).css(`background-image`, ``);
                    }
                }
            });
            $(rightPanel).find(`.normal-color.${className} .normal-color.color-picker`).on(`click`, () => {
                if ($(this).hasClass(`disabled`)) return false;
                $(rightPanel).find(`.normal-color.${className} input`).spectrum(`toggle`);
                return false;
            });

            $(rightPanel).find(`.up-color.${className} input`).spectrum('destroy');
            $(rightPanel).find(`.up-color.${className} input`).spectrum({
                color: getUpColorValue(className),
                showInput: true,
                showAlpha: true,
                containerClassName: extraClassName,
                change: (color: any) => {
                    if (color) {
                        let hex = `rgba(${color._r.toString()},${color._g.toString()},${color._b.toString()},${color._a.toString()})`;
                        setUpColorValue(className, hex);
                        $(rightPanel).find(`.${className} .up.color-picker`).css(`background-color`, hex);
                        $(rightPanel).find(`.${className} .up.color-picker`).css(`background-image`, `unset`);
                    } else {
                        setUpColorValue(className, '');
                        $(rightPanel).find(`.${className} .up.color-picker`).css(`background-color`, ``);
                        $(rightPanel).find(`.${className} .up.color-picker`).css(`background-image`, ``);
                    }
                }
            });
            $(rightPanel).find(`.up-color.${className} .up.color-picker`).on(`click`, () => {
                if ($(this).hasClass(`disabled`)) return false;
                $(rightPanel).find(`.up-color.${className} input`).spectrum(`toggle`);
                return false;
            });

            $(rightPanel).find(`.down-color.${className} input`).spectrum('destroy');
            $(rightPanel).find(`.down-color.${className} input`).spectrum({
                color: getDownColorValue(className),
                showInput: true,
                showAlpha: true,
                containerClassName: extraClassName,
                change: (color: any) => {
                    if (color) {
                        let hex = `rgba(${color._r.toString()},${color._g.toString()},${color._b.toString()},${color._a.toString()})`;
                        setDownColorValue(className, hex);
                        $(rightPanel).find(`.${className} .down.color-picker`).css(`background-color`, hex);
                        $(rightPanel).find(`.${className} .down.color-picker`).css(`background-image`, `unset`);
                    } else {
                        setDownColorValue(className, '');
                        $(rightPanel).find(`.${className} .down.color-picker`).css(`background-color`, ``);
                        $(rightPanel).find(`.${className} .down.color-picker`).css(`background-image`, ``);
                    }
                }
            });
            $(rightPanel).find(`.down-color.${className} .down.color-picker`).on(`click`, () => {
                if ($(this).hasClass(`disabled`)) return false;
                $(rightPanel).find(`.down-color.${className} input`).spectrum(`toggle`);
                return false;
            });

            $(rightPanel).find(`.radio-input.${className} input[type="radio"]`).on(`change`, (e: any) => {
                ctx.toggleColorType();
            });
        };

        setupColorPicker('one');
        setupColorPicker('two');
        setupColorPicker('three');

        const setupAutonumberic = (className: string) => {
            if (!$(rightPanel).find(`.control.${className} .text-input input`)?.length) { return; }

            const input = new AutoNumeric($(rightPanel).find(`.control.${className} .text-input input`)[0], getAutonumbericValue(className), {
                decimalPlaces: 0
            });

            input.node().addEventListener('keyup', () => {
                setAutonumbericValue(className, input.getNumber() ?? 0);
            });
        };

        const getAutonumbericValue = (className: string): number => {
            if (className == 'one') {
                return this.model.valueOne;
            }

            return this.model.valueTwo;
        }

        let updateIndicatorNameTimer: any;
        const setAutonumbericValue = (className: string, value: number) => {
            if (className == 'one') {
                this.model.valueOne = value;
            } else if (className == 'two') {
                this.model.valueTwo = value;
            }


            clearTimeout(updateIndicatorNameTimer);
            updateIndicatorNameTimer = setTimeout(() => {
                this.updateIndicatorsAbbrvWhenDirty();
            }, 500);
        }

        setupAutonumberic('one');
        setupAutonumberic('two');

        ctx.toggleColorType();
    }

    private hideAllPickerControls() {
        $(this.element).find(".font-size-picker-wrapper").removeClass('active');
    }

    private getColorGroupHtml(): string {
        const getLabel = (i: number) => {
            if (this.model.indicatorType == IndicatorType.MACD) {
                if (i == 0) {
                    return `MACD Line`;
                } else if (i == 1) {
                    return `MACD Signal`;
                }

                return `MACD Histogram`;
            }
            return `&nbsp;`;
        };

        const getHtml = (cssClass: string, color: string, upColor: string, downColor: string, colorType: IndicatorColorType, i: number): string => {
            const guid = StringHelpers.newGuid();
            return `
             <div class="section active" data-index="${i}">
                <div class="section-head">
                    <p class="section-title">${getLabel(i)}</p>
                    <div class="section-arrow">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 7" width="11" height="7" fill="none">
                            <path stroke="currentColor" stroke-width="1.3" d="M.5 1.5l5 4 5-4"></path>
                        </svg>
                    </div>
                </div>
                <div class="section-content">
                    <div class="radio-input ${cssClass} ${this.model.highChartIndicator ? 'hidden' : ''}">
                       <label class="radio-inline">
                            <input type="radio" name="colorType-${guid}" value="${IndicatorColorType.One}" ${colorType == IndicatorColorType.One ? 'checked' : ''}>One Color
                            </label>
                        <label class="radio-inline">
                            <input type="radio" name="colorType-${guid}" value="${IndicatorColorType.UpDown}" ${colorType == IndicatorColorType.One ? '' : 'checked'}>Up / Down Color
                        </label>
                    </div>
                    <div class="control normal-color ${colorType == IndicatorColorType.One ? '' : 'disabled'} ${cssClass}">
                        <span class="label-inline" style="width: 80px; margin-right: 4px;">Color</span>
                        <div class="color-picker-wrapper mgr-8">
                            <div class="indicator normal-color color-picker" style="background-color: ${color}; background-image: unset;">
                                <input class='normal' style="position: relative; top: 20px;height: 1px; width: 1px; visibility: hidden; opacity: 0;" />
                            </div>
                        </div>
                    </div>
                    <div class="control up-color ${colorType == IndicatorColorType.One ? 'disabled' : ''}  ${this.model.highChartIndicator ? 'hidden' : ''} ${cssClass}">
                        <span class="label-inline" style="width: 80px; margin-right: 4px;">Up Color</span>
                        <div class="color-picker-wrapper mgr-8">
                            <div class="indicator up color-picker" style="background-color: ${upColor}; background-image: unset;">
                                <input class='up' style="position: relative; top: 20px;height: 1px; width: 1px; visibility: hidden; opacity: 0;" />
                            </div>
                        </div>
                    </div>
                    <div class="control down-color ${colorType == IndicatorColorType.One ? 'disabled' : ''}  ${this.model.highChartIndicator ? 'hidden' : ''} ${cssClass}">
                        <span class="label-inline" style="width: 80px; margin-right: 4px;">Down Color</span>
                        <div class="color-picker-wrapper mgr-8">
                            <div class="indicator down color-picker" style="background-color: ${downColor}; background-image: unset;">
                                <input class='down' style="position: relative; top: 20px;height: 1px; width: 1px; visibility: hidden; opacity: 0;" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            `;
        };

        let html = getHtml('color one', this.model.colorOne, this.model.upColorOne, this.model.downColorOne, this.model.colorTypeOne, 0);

        if (this.model.indicatorType == IndicatorType.MACD) {
            html += getHtml('color two', this.model.colorTwo, this.model.upColorTwo, this.model.downColorTwo, this.model.colorTypeTwo, 1);
            html += getHtml('color three', this.model.colorThree, this.model.upColorThree, this.model.downColorThree, this.model.colorTypeThree, 2);
        } else if (this.model.indicatorType == IndicatorType.HighChartStochastic || this.model.indicatorType == IndicatorType.BBands || this.model.indicatorType == IndicatorType.KeltnerChannel) {
            html += getHtml('color two', this.model.colorTwo, this.model.upColorTwo, this.model.downColorTwo, this.model.colorTypeTwo, 1);
        }

        return html;
    }

    private getValueGroupHtml(): string {
        const hasValueOne = this.model.valueOne != null && this.model.valueOne >= 0;
        if (!hasValueOne) return ``;

        let oneValueLabel = 'Periods';
        let twoValueLabel = 'Standard Deviation';
        if (this.model.indicatorType == IndicatorType.MACD) {
            oneValueLabel = 'Fast EMA';
            twoValueLabel = 'Slow EMA';
        }

        let html = `
            <div class="control value one">
                <span class="label-inline" style="width: 80px; margin-right: 4px;">${oneValueLabel}</span>
                <div class="text-input mgr-8">
                    <input type="text" inputmode="decimal" pattern="[0-9]*" value="${this.model.valueOne}">
                </div>
            </div> `;

        const hasValueTwo = this.model.valueTwo != null && this.model.valueTwo >= 0;
        if (hasValueTwo) {
            html += `
            <div class="control value two">
                <span class="label-inline" style="width: 80px; margin-right: 4px;">${twoValueLabel}</span>
                <div class="text-input mgr-8">
                    <input type="text" inputmode="decimal" pattern="[0-9]*" value="${this.model.valueTwo}">
                </div>
            </div> `;
        }

        return html;
    }

    private closePicker() {
        const rightPanel = this.element?.querySelector<HTMLElement>('.right-panel');
        if (!rightPanel || !rightPanel.innerHTML) return;

        $(rightPanel).find(".font-size-picker-wrapper.chartarea").removeClass('active');
    }

    private onBodyClick() {
        this.base.domEvents.onBodyClick(this.id, (ev: any) => {
            const target = ev.target as HTMLElement;
            if (target.classList.contains('font-size-picker-wrapper') || !!target.closest('.font-size-picker-wrapper')) {
                return;
            }

            this.closePicker();
        });
    }

    private setContentMaxHeight() {
        this.element?.findFirst('.content')?.setAttribute('style' ,`overflow-y: auto; max-height: ${this.base.chartWrapper?.clientHeight - 150}px`);
    }

    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            this.setContentMaxHeight();
        });
    }
}